import { useState } from 'react';
import {
  Alert as MuiAlert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, Autorenew, ContentCopy, DeleteOutline, Edit, Replay, Webhook } from '@mui/icons-material';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as wh from '@/api/webhooks';
import type { WebhookDeliveryStatus, WebhookSubscription } from '@/api/webhooks';
import { toApiError } from '@/api/client';
import { ApiProductRouteGuard } from '@/components/ApiProductRouteGuard';
import { EmptyState } from '@/components/EmptyState';
import { LoadingScreen } from '@/components/LoadingScreen';
import { formatDateTime } from '@/lib/formatters';

type ChipColor = 'warning' | 'success' | 'error' | 'default';

export const Route = createFileRoute('/_authenticated/webhooks/')({
  component: WebhooksPage,
});

const DELIVERY_COLOR: Record<WebhookDeliveryStatus, ChipColor> = {
  Pending: 'warning',
  Delivered: 'success',
  Failed: 'error',
  DeadLettered: 'default',
};
const DELIVERY_LABEL: Record<WebhookDeliveryStatus, string> = {
  Pending: 'Pendiente',
  Delivered: 'Entregado',
  Failed: 'Falló',
  DeadLettered: 'Dead-letter',
};

type Toast = { message: string; severity: 'success' | 'error' };

function WebhooksPage(): JSX.Element {
  return (
    <ApiProductRouteGuard>
      <WebhooksContent />
    </ApiProductRouteGuard>
  );
}

function WebhooksContent(): JSX.Element {
  const [tab, setTab] = useState<'endpoints' | 'deliveries'>('endpoints');
  const [toast, setToast] = useState<Toast | null>(null);

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Webhook color="primary" /> Webhooks
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Suscripciones a eventos con firma HMAC, reintentos y dead-letter.
          </Typography>
        </Box>
      </Stack>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Endpoints" value="endpoints" />
        <Tab label="Entregas" value="deliveries" />
      </Tabs>

      {tab === 'endpoints' ? <Endpoints onToast={setToast} /> : <Deliveries onToast={setToast} />}

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <MuiAlert severity={toast?.severity ?? 'info'} onClose={() => setToast(null)} variant="filled">
          {toast?.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}

function Endpoints({ onToast }: { onToast: (t: Toast) => void }): JSX.Element {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<WebhookSubscription | 'new' | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<WebhookSubscription | null>(null);

  const listQ = useQuery({ queryKey: ['webhooks'], queryFn: wh.listWebhooks });
  const invalidate = (): void => void queryClient.invalidateQueries({ queryKey: ['webhooks'] });
  const onError = (err: unknown): void => onToast({ message: toApiError(err).message, severity: 'error' });

  const toggleMut = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) => wh.setWebhookEnabled(id, enabled),
    onSuccess: () => invalidate(),
    onError,
  });
  const rotateMut = useMutation({
    mutationFn: (id: string) => wh.rotateWebhookSecret(id),
    onSuccess: (res) => { invalidate(); setSecret(res.secret); },
    onError,
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => wh.deleteWebhook(id),
    onSuccess: () => { setConfirmDelete(null); invalidate(); onToast({ message: 'Webhook eliminado.', severity: 'success' }); },
    onError,
  });

  if (listQ.isLoading) return <LoadingScreen message="Cargando webhooks…" />;
  if (listQ.error) return <MuiAlert severity="error">{toApiError(listQ.error).message}</MuiAlert>;

  const hooks = listQ.data ?? [];

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setEditing('new')}>Nuevo endpoint</Button>
      </Stack>

      {hooks.length === 0 ? (
        <EmptyState
          icon={<Webhook fontSize="inherit" />}
          title="Sin endpoints"
          description="Registrá una URL para recibir eventos de dominio (comprobantes, certificados, pagos…)."
          actionLabel="Nuevo endpoint"
          onAction={() => setEditing('new')}
        />
      ) : (
        <Stack spacing={1.5}>
          {hooks.map((h) => (
            <Paper key={h.id} variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" sx={{ fontFamily: 'var(--font-mono)', fontWeight: 600, wordBreak: 'break-all' }}>
                    {h.url}
                  </Typography>
                  {h.description && <Typography variant="caption" color="text.secondary">{h.description}</Typography>}
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
                    {h.eventTypes.map((e) => <Chip key={e} size="small" variant="outlined" label={e} />)}
                  </Stack>
                </Box>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Tooltip title={h.status === 'Active' ? 'Activo' : 'Deshabilitado'}>
                    <Switch
                      size="small"
                      checked={h.status === 'Active'}
                      disabled={toggleMut.isPending}
                      onChange={(e) => toggleMut.mutate({ id: h.id, enabled: e.target.checked })}
                    />
                  </Tooltip>
                  <Tooltip title="Editar"><IconButton size="small" onClick={() => setEditing(h)}><Edit fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Rotar secret"><span><IconButton size="small" disabled={rotateMut.isPending} onClick={() => rotateMut.mutate(h.id)}><Autorenew fontSize="small" /></IconButton></span></Tooltip>
                  <Tooltip title="Eliminar"><IconButton size="small" color="error" onClick={() => setConfirmDelete(h)}><DeleteOutline fontSize="small" /></IconButton></Tooltip>
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      {editing && (
        <EndpointDialog
          webhook={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(newSecret) => {
            setEditing(null);
            invalidate();
            if (newSecret) setSecret(newSecret);
            else onToast({ message: 'Endpoint actualizado.', severity: 'success' });
          }}
          onError={onError}
        />
      )}

      {secret && <SecretDialog secret={secret} onClose={() => setSecret(null)} onCopied={() => onToast({ message: 'Copiado.', severity: 'success' })} />}

      {confirmDelete && (
        <Dialog open onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Eliminar webhook</DialogTitle>
          <DialogContent>
            <Typography variant="body2">Se eliminará el endpoint <strong>{confirmDelete.url}</strong>. Dejará de recibir eventos.</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDelete(null)}>Cancelar</Button>
            <Button color="error" variant="contained" disabled={deleteMut.isPending} onClick={() => deleteMut.mutate(confirmDelete.id)}>Eliminar</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}

function EndpointDialog({
  webhook,
  onClose,
  onSaved,
  onError,
}: {
  webhook: WebhookSubscription | null;
  onClose: () => void;
  onSaved: (secret: string | null) => void;
  onError: (err: unknown) => void;
}): JSX.Element {
  const isNew = webhook === null;
  const [url, setUrl] = useState(webhook?.url ?? '');
  const [description, setDescription] = useState(webhook?.description ?? '');
  const [eventTypes, setEventTypes] = useState<string[]>(webhook?.eventTypes ?? []);

  const mut = useMutation<wh.WebhookSecretResponse | wh.WebhookSubscription>({
    mutationFn: () => {
      const input = { url: url.trim(), description: description.trim() || null, eventTypes };
      return isNew ? wh.createWebhook(input) : wh.updateWebhook(webhook!.id, input);
    },
    onSuccess: (res) => onSaved('secret' in res ? res.secret : null),
    onError,
  });

  const toggle = (e: string): void => setEventTypes((cur) => (cur.includes(e) ? cur.filter((x) => x !== e) : [...cur, e]));
  const validUrl = /^https:\/\/.+/.test(url.trim());
  const valid = validUrl && eventTypes.length > 0;

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isNew ? 'Nuevo endpoint' : 'Editar endpoint'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          <TextField
            label="URL (https)"
            fullWidth
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://api.tu-sistema.com/webhooks/tilde"
            error={url.length > 0 && !validUrl}
            helperText={url.length > 0 && !validUrl ? 'Debe ser una URL https://' : ' '}
            autoFocus
          />
          <TextField label="Descripción (opcional)" fullWidth value={description} onChange={(e) => setDescription(e.target.value)} />
          <Box>
            <Typography variant="subtitle2" gutterBottom>Eventos</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
              {wh.WEBHOOK_EVENT_TYPES.map((e) => (
                <FormControlLabel key={e} control={<Checkbox size="small" checked={eventTypes.includes(e)} onChange={() => toggle(e)} />} label={<Typography variant="body2">{e}</Typography>} />
              ))}
            </Box>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" disabled={!valid || mut.isPending} onClick={() => mut.mutate()}>
          {mut.isPending ? <CircularProgress size={18} /> : isNew ? 'Crear' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Deliveries({ onToast }: { onToast: (t: Toast) => void }): JSX.Element {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<WebhookDeliveryStatus | 'All'>('All');

  const statusParam = filter === 'All' ? undefined : filter;
  const listQ = useQuery({
    queryKey: ['webhook-deliveries', filter],
    queryFn: () => wh.listWebhookDeliveries(statusParam, 0, 100),
  });

  const replayMut = useMutation({
    mutationFn: (id: string) => wh.replayWebhookDelivery(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['webhook-deliveries'] });
      onToast({ message: 'Reintento encolado.', severity: 'success' });
    },
    onError: (err) => onToast({ message: toApiError(err).message, severity: 'error' }),
  });

  const items = listQ.data?.items ?? [];

  return (
    <Box>
      <ToggleButtonGroup size="small" exclusive value={filter} onChange={(_, v: typeof filter | null) => v && setFilter(v)} sx={{ mb: 2 }}>
        <ToggleButton value="All">Todas</ToggleButton>
        <ToggleButton value="Pending">Pendientes</ToggleButton>
        <ToggleButton value="Delivered">Entregadas</ToggleButton>
        <ToggleButton value="Failed">Fallidas</ToggleButton>
        <ToggleButton value="DeadLettered">Dead-letter</ToggleButton>
      </ToggleButtonGroup>

      {listQ.isLoading ? (
        <LoadingScreen message="Cargando entregas…" />
      ) : listQ.error ? (
        <MuiAlert severity="error">{toApiError(listQ.error).message}</MuiAlert>
      ) : items.length === 0 ? (
        <EmptyState icon={<Webhook fontSize="inherit" />} title="Sin entregas" description="Todavía no hay entregas para este filtro." />
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Evento</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Intentos</TableCell>
                <TableCell>Última respuesta</TableCell>
                <TableCell>Actualizado</TableCell>
                <TableCell align="right">Acción</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((d) => (
                <TableRow key={d.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'var(--font-mono)' }}>{d.eventType}</Typography>
                    <Typography variant="caption" color="text.secondary">{d.eventId}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" color={DELIVERY_COLOR[d.status]} label={DELIVERY_LABEL[d.status]} />
                  </TableCell>
                  <TableCell align="right">{d.attemptCount}</TableCell>
                  <TableCell>
                    {d.lastResponseStatus ? (
                      <Typography variant="caption">HTTP {d.lastResponseStatus}</Typography>
                    ) : d.lastError ? (
                      <Tooltip title={d.lastError}><Typography variant="caption" color="error">error</Typography></Tooltip>
                    ) : (
                      <Typography variant="caption" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell><Typography variant="caption" color="text.secondary">{formatDateTime(d.updatedAt)}</Typography></TableCell>
                  <TableCell align="right">
                    {(d.status === 'Failed' || d.status === 'DeadLettered') && (
                      <Tooltip title="Reintentar entrega">
                        <span>
                          <IconButton size="small" disabled={replayMut.isPending} onClick={() => replayMut.mutate(d.id)}>
                            <Replay fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

function SecretDialog({ secret, onClose, onCopied }: { secret: string; onClose: () => void; onCopied: () => void }): JSX.Element {
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Secret de firma</DialogTitle>
      <DialogContent dividers>
        <MuiAlert severity="warning" sx={{ mb: 2 }}>
          Guardá este <strong>signing secret</strong> ahora: se muestra una sola vez. Se usa para verificar la firma HMAC de cada entrega.
        </MuiAlert>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ flex: 1, p: 1, borderRadius: 1, bgcolor: 'action.hover', fontFamily: 'var(--font-mono)', fontSize: 13, wordBreak: 'break-all' }}>
            {secret}
          </Box>
          <Tooltip title="Copiar">
            <IconButton size="small" onClick={() => void navigator.clipboard?.writeText(secret).then(onCopied)}><ContentCopy fontSize="small" /></IconButton>
          </Tooltip>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>Listo, lo guardé</Button>
      </DialogActions>
    </Dialog>
  );
}
